import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { useAppTranslation } from '../../hooks'
import { getAppBarState } from 'src/store'
import { CustomAutocomplete } from './CustomAutoComplete'
import { fetchLocationLineCheck } from '../../services/financial-flows/locationLineCheck'
import { LocationLineCheckItem } from '../../services/financial-flows/types'
import { CircularProgress, Box, Typography } from '@mui/material'

export interface LocationLineCheckOption {
  value: number
  label: string
}

export interface LocationLineCheckSelectProps {
  disabled?: boolean
  value: LocationLineCheckOption | null
  onChange?: (value: LocationLineCheckOption | null) => void
  getDefaultValue?: (value: LocationLineCheckOption | null) => void
  disableClearable?: boolean
  selectDefaultLocation?: boolean
}

export const LocationLineCheckSelect: React.FC<LocationLineCheckSelectProps> = ({
  disabled = false,
  onChange,
  value,
  getDefaultValue,
  disableClearable = false,
  selectDefaultLocation = true,
}) => {
  const { label } = useAppTranslation()
  const { user } = getAppBarState().appState
  const [locations, setLocations] = useState<LocationLineCheckItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch locations from API
  const loadLocations = useCallback(async () => {
    if (!user?.profile?.sub) {
      setError('No se encontró el ID del usuario')
      return
    }

    setLoading(true)
    setError(null)

    try {
      const response = await fetchLocationLineCheck(user.profile.sub)
      setLocations(response.data)
    } catch (err) {
      console.error('Error loading locations:', err)
      setError(err instanceof Error ? err.message : 'Error al cargar las ubicaciones')
      setLocations([])
    } finally {
      setLoading(false)
    }
  }, [user?.profile?.sub])

  // Load locations on mount
  useEffect(() => {
    loadLocations()
  }, [loadLocations])

  // Create options array with value and label
  const locationOptions = useMemo<LocationLineCheckOption[]>(() => {
    return locations.map(location => ({
      value: location.Id,
      label: location.Name,
    }))
  }, [locations])

  // Auto-select first location when locations are available and no value is set
  useEffect(() => {
    if (selectDefaultLocation && locationOptions.length > 0 && !value && getDefaultValue) {
      const firstLocation = locationOptions[0]
      getDefaultValue(firstLocation)
    }
  }, [selectDefaultLocation, locationOptions, value, getDefaultValue])

  // Find current selected option based on external value
  const selectedOption = useMemo(() => {
    if (!value) return null
    return locationOptions.find(option => option.value === value.value) || value
  }, [value, locationOptions])

  // Show loading state
  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="56px">
        <CircularProgress size={24} />
      </Box>
    )
  }

  // Show error state
  if (error) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="56px">
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    )
  }

  return (
    <CustomAutocomplete
      data={locationOptions}
      value={selectedOption}
      onChangeValue={(selectedOption) => {
        if (onChange) {
          onChange(selectedOption || null)
        }
      }}
      placeholder={label('AGENCIA')}
      keyLabel="label"
      label={label('AGENCIA')}
      disableClearable={disableClearable}
      disabled={disabled}
    />
  )
}

export default LocationLineCheckSelect
